import {AppRootStore, IAppRootStore} from './app.root.store';
import {LocalizableFieldModel, ILocalizable} from './models/LocalizableFieldModel';
import {IAppBootstrapParams, IFieldFactory} from './app.contracts';
import {getBrowserInfo} from '@techsee/techsee-client-infra/lib/shared/browser-info';
import {ITsEnvironmentDetect, TsEnvironmentDetect} from '@techsee/techsee-common/lib/helpers/ts-environment-detect';
import {PromiseUtilsService} from '@techsee/techsee-client-services/lib/services/PromiseUtilsService';
import {PlatformType} from '@techsee/techsee-common/lib/constants/utils.constant';
import {DASHBOARD_APP_TRACER, getAppTracer} from '../app.tracer';
import {
    enableTracerByName,
    registerTracerOutput,
    traceToConsoleOutputFunction
} from '@techsee/techsee-common/lib/core/Tracer';
import {TECHSEE_MEDIA_TRACER} from '@techsee/techsee-media-service/lib/MediaUtils/MediaTracer';
import {TechseeBaseController} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-controller';
import {sendSentryLog} from '../error-reporting';

const trace = getAppTracer('app.bootstrap');
import {setupReactAuthErrorInterceptor} from './services/auth-interceptor/react-auth-interceptor.service';

let rootStore: IAppRootStore;
let fieldModelFactory: IFieldFactory;

/**
 * Full React bootstrap (no Angular dependencies)
 * Used for pure React mode
 */
export async function initRootStore(dependencies: IAppBootstrapParams): Promise<IAppRootStore> {
    //First we init the root store, wich includes all application wide single instance services.
    const store = new AppRootStore(dependencies);
    const browserInfo = getBrowserInfo();

    rootStore = store;

    setupReactAuthErrorInterceptor();

    await rootStore.initRootServices({
        env: {
            API_URL,
            SOCKET_URL,
            STATS_API_URL,
            MOBILE_CLIENT_URL
        },
        browserInfo
    });

    //Then we init the factory for creating the localizable fields
    fieldModelFactory = (translationKey?: string): ILocalizable => {
        const field = new LocalizableFieldModel(rootStore.localizationService, rootStore.appState);

        field.labelKey = translationKey || '';

        return field;
    };

    return rootStore;
}

//Returns applications root store
export function getRootStore(): IAppRootStore {
    if (!rootStore) {
        throw 'root store was not initialized, insure you have bootstrapped the application';
    }

    return rootStore;
}

// Expose getRootStore to window for debugging (development only)
if (process.env.NODE_ENV === 'development') {
    (window as any).getRootStore = getRootStore;
}

//Uses field model factory to create localizable fields
export function createFieldModel(translationKey?: string): ILocalizable {
    if (!fieldModelFactory) {
        throw new Error('Field model factory is not registered');
    }

    return fieldModelFactory(translationKey);
}

/**
 * Environment Detection
 * Migrated from app.module.js setEnvironmentDetect
 */
export async function createEnvironmentDetect(): Promise<ITsEnvironmentDetect> {
    let environmentDetect: ITsEnvironmentDetect | null = null;

    try {
        if (TsEnvironmentDetect.create) {
            environmentDetect = await PromiseUtilsService.startPromiseWithTimeout(TsEnvironmentDetect.create, 5000);

            if (environmentDetect) {
                trace.info('TsEnvironmentDetect.create success');
                return environmentDetect;
            }
        }
    } catch (error) {
        trace.error('TsEnvironmentDetect.create error, moving to static TsEnvironmentDetect', error);
    }

    // Fallback to static creation
    if (!environmentDetect) {
        trace.info('failed using TsEnvironmentDetect.create, moving to static TsEnvironmentDetect');
        environmentDetect = new TsEnvironmentDetect();
        trace.info('TsEnvironmentDetect constructor success');
    }

    return environmentDetect;
}

/**
 * Initialize Global Exception Handler
 * Migrated from services/error-handler/global-exception-handler.ts
 */
export function initGlobalExceptionHandler(): void {
    const serializeError = (err: any) => {
        if (err == null || typeof err !== 'object') {
            return err;
        }

        if (typeof err.message === 'string' && ('stack' in err || err instanceof Error)) {
            return {message: err.message, stack: err.stack, status: err.status};
        }

        return err;
    };

    window.onerror = (msg: string | Event, src?: string, line?: number, col?: number, error?: Error) => {
        const meta = {
            clientType: PlatformType.dashboard,
            error: {msg, src, line, col, error: serializeError(error)}
        };

        getRootStore().eventLogService.dashboardInitGlobalExceptionHandlerError(meta);
    };

    window.addEventListener('unhandledrejection', (event) => {
        getRootStore().eventLogService.dashboardInitGlobalExceptionHandlerError({
            clientType: PlatformType.dashboard,
            error: {
                msg: 'Unhandled Promise Rejection',
                reason: serializeError(event.reason)
            }
        });
    });
}

export function configureDashboardTracing(): void {
    const rootStore = getRootStore();

    if (String(rootStore.browserUtilsService.getFromLocalStorage('TRACE_TO_CONSOLE')) === 'true') {
        registerTracerOutput(traceToConsoleOutputFunction());
    }

    registerTracerOutput(rootStore.tsExtraLoggingService.traceToEventLogOutputFunction);
    enableTracerByName(TECHSEE_MEDIA_TRACER);
    enableTracerByName(DASHBOARD_APP_TRACER);

    rootStore.tsExtraLoggingService.init(null as any, rootStore.eventLogService);
}

/**
 * Fail-safe error event logger.
 * Enriches events with accountId, userId, and roomId when available.
 */
export function addErrorEventLog(logType: string, meta: Record<string, any>): void {
    let rootStore;

    try {
        rootStore = getRootStore();

        const accountId = rootStore?.appState?.accountId || undefined;
        const userId = rootStore?.appState?.agentId || undefined;
        const roomId = rootStore?.chatApi?.roomId || rootStore?.eventsLogService?.room || undefined;

        rootStore.eventsLogService.error({logType, meta, accountId, userId, roomId});
    } catch (e) {
        sendSentryLog(e);
    }
}

export function configureUiInfra(): void {
    const rootStore = getRootStore();

    TechseeBaseController.setInfraTools({
        infraAppEventsHub: () => rootStore.appEventsHub as any,
        infraEventsLog: () => rootStore.eventLogServiceInfra,
        infraLocalization: () => ({
            translate: rootStore.localizationService.translate.bind(rootStore.localizationService) as any,
            dir: rootStore.localizationService.getTextDirection(),
            isRTL: rootStore.localizationService.isRTL()
        }),
        infraAppearance: () => ({
            useNewInterface: rootStore.tsInterfaceHelperService.useNewInterface
        }),
        infraEnvironment: () => rootStore.environmentService
    });
}
